import { currencyFractionDigits } from '../../../shared/utils/commercial-money';
import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MenuItem, MessageService } from 'primeng/api';
import { catchError, forkJoin, of } from 'rxjs';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { FieldsetModule } from 'primeng/fieldset';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { IftaLabelModule } from 'primeng/iftalabel';
import { PanelModule } from 'primeng/panel';
import { TagModule } from 'primeng/tag';
import { OrdersService } from '../../order.service';
import { PurchaseOrder, PurchaseOrderActionMetadata } from '../purchase-order.model';
import { PurchaseOrderLine } from '../purchase-order-line.model';
import { FlexFieldValue } from 'src/app/shared/flex-fields/flex-field.model';
import { FlexFieldViewComponent } from 'src/app/shared/flex-fields/flex-field-view/flex-field-view.component';
import { SessionStorageService } from 'src/app/services/session-storage.service';
import { PageShellComponent } from 'src/app/shared/page-layout/page-shell.component';
import { PageDetailHeroComponent } from 'src/app/shared/page-layout/page-detail-hero.component';
import { PageDataPanelComponent } from 'src/app/shared/page-layout/page-data-panel.component';
import { PageMetricStripComponent } from 'src/app/shared/page-layout/page-metric-strip.component';
import { PageMetric } from 'src/app/shared/page-layout/page-layout.models';
import { TooltipModule } from 'primeng/tooltip';
import { TabsModule } from 'primeng/tabs';
import { EditorModule } from 'primeng/editor';
import { FileUploadModule } from 'primeng/fileupload';
import { ProgressBarModule } from 'primeng/progressbar';
import { ToastModule } from 'primeng/toast';
import { DialogModule } from 'primeng/dialog';
import { OrderActivity } from '../../order-workbench.model';
import { getOrderStatusMetadata } from '../../order-status-metadata';
import { FirebaseStorageService } from 'src/app/services/firebase-storage.service';
import { PurchaseOrderAttachment } from '../purchase-order-attachment.model';
import { PurchaseOrderReceipt } from '../purchase-order-receipt.model';
import { EnterpriseFeatureService } from 'src/app/services/enterprise-features.service';
import { PermissionService } from 'src/app/security/permission.service';
import { app_permissions } from 'src/app/security/app-permissions';
import { LogisticsProjectsService } from 'src/app/logistics-projects/logistics-projects.service';
import { LogisticsProjectSummary } from 'src/app/logistics-projects/models/logistics-project.model';
import {
    VendorBillReadinessResponse
} from '../purchase-order-vendor-bill.model';

interface HeroLifecycleAction {
    code: string;
    label: string;
    icon: string;
    severity: 'secondary' | 'success' | 'danger' | 'warn' | 'info' | 'help';
    outlined?: boolean;
}

@Component({
    selector: 'app-view-purchase-order',
    standalone: true,
    templateUrl: './view-purchase-order.component.html',
    styleUrls: ['./view-purchase-order.component.css', '../../order-attachment-delete-dialog.css'],
    imports: [
        CommonModule,
        FormsModule,
        RouterModule,
        BreadcrumbModule,
        FieldsetModule,
        InputGroupModule,
        InputGroupAddonModule,
        InputTextModule,
        TextareaModule,
        TableModule,
        ButtonModule,
        IftaLabelModule,
        PanelModule,
        FlexFieldViewComponent,
        TagModule,
        PageShellComponent,
        PageDetailHeroComponent,
        PageDataPanelComponent,
        PageMetricStripComponent,
        TooltipModule,
        TabsModule,
        EditorModule,
        FileUploadModule,
        ProgressBarModule,
        ToastModule,
        DialogModule
    ],
    providers: [MessageService]
})
export class ViewPurchaseOrderComponent implements OnInit {
    private readonly tabKeys = ['overview', 'lines', 'approvals', 'receipts', 'matching', 'vendor-bills', 'audit', 'notes'] as const;
    readonly heroLifecycleActions: HeroLifecycleAction[] = [
        { code: 'SUBMIT', label: 'Submit', icon: 'pi pi-send', severity: 'secondary', outlined: true },
        { code: 'APPROVE', label: 'Approve', icon: 'pi pi-check-circle', severity: 'success', outlined: true },
        { code: 'REJECT', label: 'Reject', icon: 'pi pi-times-circle', severity: 'danger', outlined: true },
        { code: 'CONFIRM', label: 'Confirm', icon: 'pi pi-verified', severity: 'success' },
        { code: 'SEND_TO_SUPPLIER', label: 'Send', icon: 'pi pi-envelope', severity: 'info', outlined: true },
        { code: 'ACKNOWLEDGE', label: 'Acknowledge', icon: 'pi pi-check-square', severity: 'secondary', outlined: true },
        { code: 'HOLD', label: 'Hold', icon: 'pi pi-lock', severity: 'warn', outlined: true },
        { code: 'RELEASE_HOLD', label: 'Release', icon: 'pi pi-lock-open', severity: 'info', outlined: true },
        { code: 'CLOSE', label: 'Close', icon: 'pi pi-check', severity: 'secondary', outlined: true },
        { code: 'CANCEL', label: 'Cancel', icon: 'pi pi-ban', severity: 'danger', outlined: true }
    ];

    breadcrumbItems: MenuItem[] = [];
    homeBreadcrumb: MenuItem = { icon: 'pi pi-home', routerLink: '/' };

    purchaseOrder: PurchaseOrder | undefined;
    activity: OrderActivity[] = [];
    purchaseActionMetadata: PurchaseOrderActionMetadata | undefined;
    currencyCode = 'SAR';
    activeTabIndex = 0;
    actionLoading = false;
    actionDialogVisible = false;
    pendingAction = '';
    actionReason = '';
    notesDraft = '';
    notesEditing = false;
    notesSaving = false;
    attachmentUploadProgress = 0;
    attachmentUploading = false;
    attachmentToDelete?: PurchaseOrderAttachment;
    attachmentDeleteDialogVisible = false;
    attachmentDeleteReason = '';
    attachmentDeleteError = '';
    attachmentDeleting = false;
    purchaseAttachments: PurchaseOrderAttachment[] = [];
    purchaseReceipts: PurchaseOrderReceipt[] = [];
    vendorBillReadiness: VendorBillReadinessResponse | undefined;
    payableStagingLoading = false;
    projectNames: Record<number, string> = {};

    constructor(
        private readonly route: ActivatedRoute,
        private readonly router: Router,
        private readonly sessionStorageService: SessionStorageService,
        private readonly ordersService: OrdersService,
        private readonly messageService: MessageService,
        private readonly firebaseStorageService: FirebaseStorageService,
        private readonly enterpriseFeatureService: EnterpriseFeatureService,
        private readonly permissionService: PermissionService,
        private readonly logisticsProjectsService: LogisticsProjectsService
    ) { }

    ngOnInit(): void {
        this.breadcrumbItems = [
            { label: 'Orders', routerLink: '/order/purchase-order' },
            { label: 'View Purchase Order' }
        ];
        if (!this.enterpriseFeatureService.isLoaded()) {
            this.enterpriseFeatureService.getAllSubscriptions().subscribe();
        }

        const owningCompany = this.sessionStorageService.getCompany();
        const currentCountry = this.sessionStorageService.getCountryList()?.find(country => country.id === owningCompany?.countryId);
        if (currentCountry?.currencyCode) {
            this.currencyCode = currentCountry.currencyCode;
        }

        this.route.params.subscribe(params => {
            const id = Number(params['id']);
            if (id) {
                forkJoin({
                    order: this.ordersService.getPurchaseOrderById(id),
                    activity: this.ordersService.getPurchaseOrderActivity(id).pipe(catchError(() => of([]))),
                    actions: this.ordersService.getPurchaseOrderActions(id).pipe(catchError(() => of({
                        availableActions: [],
                        requiresReason: [],
                        blockedReasons: {}
                    }))),
                    vendorBillReadiness: this.ordersService.getPurchaseOrderVendorBillReadiness(id)
                        .pipe(catchError(() => of(undefined))),
                    receipts: this.ordersService.getPurchaseOrderReceipts(id, this.sessionStorageService.getCompany()?.id || 0)
                        .pipe(catchError(() => of([])))
                }).subscribe(({ order, activity, actions, vendorBillReadiness, receipts }) => {
                    this.purchaseOrder = order;
                    this.activity = activity ?? [];
                    this.purchaseActionMetadata = actions || {};
                    this.currencyCode = order.currencyCode || this.currencyCode;
                    this.notesDraft = order.notes || '';
                    this.purchaseAttachments = order.attachments ?? [];
                    this.purchaseReceipts = receipts ?? [];
                    this.vendorBillReadiness = vendorBillReadiness;
                    this.loadProjectNames(order);
                });
            }
        });
        this.route.queryParamMap.subscribe(params => {
            this.activeTabIndex = this.resolveTabIndex(params.get('tab'));
        });
    }

    createReceipt(): void {
        if (this.purchaseOrder?.purchaseOrderID) this.router.navigate(['/order/purchase-order/view', this.purchaseOrder.purchaseOrderID, 'receipts', 'new']);
    }

    viewReceipt(receipt: PurchaseOrderReceipt): void {
        if (this.purchaseOrder?.purchaseOrderID && receipt.id) this.router.navigate(['/order/purchase-order/view', this.purchaseOrder.purchaseOrderID, 'receipts', receipt.id]);
    }

    hasHeaderFlexFields(): boolean {
        return (this.purchaseOrder?.flexFieldValues?.length ?? 0) > 0;
    }

    getHeaderFlexFields(): FlexFieldValue[] {
        return this.purchaseOrder?.flexFieldValues ?? [];
    }

    getLineFlexFields(line: PurchaseOrderLine | undefined): FlexFieldValue[] {
        return line?.flexFieldValues ?? [];
    }

    isBillGenerated(): boolean {
        if (this.purchaseOrder?.billGenerated) {
            return true;
        }
        const state = (this.purchaseOrder?.state || '').toUpperCase();
        if (state === 'BILLED') {
            return true;
        }
        const billingStatus = (this.purchaseOrder?.billingStatus || '').toUpperCase();
        if (['BILL_DRAFTED', 'BILLED', 'PARTIALLY_BILLED'].includes(billingStatus)) {
            return true;
        }
        if (this.vendorBillReadiness?.billId || this.vendorBillReadiness?.billNumber) {
            return true;
        }
        return false;
    }

    isEditLocked(): boolean {
        return this.isBillGenerated();
    }

    editLockedTooltip(): string {
        if (this.isBillGenerated()) {
            return 'Order editing is locked: Vendor bill has already been generated.';
        }
        return 'Edit purchase order lines and details';
    }

    editOrder(): void {
        if (this.purchaseOrder?.purchaseOrderID) {
            if (this.isEditLocked()) {
                this.messageService.add({
                    severity: 'warn',
                    summary: 'Editing Locked',
                    detail: this.editLockedTooltip()
                });
                return;
            }
            this.router.navigate(['/order/purchase-order/edit', this.purchaseOrder.purchaseOrderID]);
        }
    }

    hasLogisticsProject(): boolean {
        return !!this.purchaseOrder?.logisticsProjectId;
    }

    viewLogisticsProject(): void {
        if (this.purchaseOrder?.logisticsProjectId) {
            this.router.navigate(['/logistics-projects', this.purchaseOrder.logisticsProjectId]);
        }
    }

    logisticsProjectName(projectId?: number): string {
        if (!projectId) {
            return '-';
        }
        if (projectId === this.purchaseOrder?.logisticsProjectId && this.purchaseOrder.logisticsProjectName) {
            return this.purchaseOrder.logisticsProjectName;
        }
        return this.projectNames[projectId] || 'Linked project';
    }

    private loadProjectNames(order: PurchaseOrder): void {
        const projectIds = new Set<number>([
            order.logisticsProjectId,
            ...(order.purchaseOrderLines || []).map(line => line.logisticsProjectId)
        ].filter((id): id is number => !!id));
        const companyId = order.owningCompanyId || this.sessionStorageService.getCompany()?.id;
        if (!companyId || !projectIds.size) {
            this.projectNames = {};
            return;
        }

        this.logisticsProjectsService.lookupProjects(companyId, '', Array.from(projectIds)).pipe(
            catchError(() => of([] as LogisticsProjectSummary[]))
        ).subscribe(projects => {
            this.projectNames = projects.reduce<Record<number, string>>((names, project) => {
                if (project.projectId && project.projectName) {
                    names[project.projectId] = project.projectName;
                }
                return names;
            }, {});
        });
    }

    getSubTotal(): number {
        if (this.purchaseOrder?.subtotalAmount !== undefined && this.purchaseOrder.subtotalAmount !== null) {
            return Number(this.purchaseOrder.subtotalAmount);
        }
        if (!this.purchaseOrder?.purchaseOrderLines?.length) {
            return 0;
        }
        return this.purchaseOrder.purchaseOrderLines.reduce((total, line) => total + (line.quantity * line.unitPrice), 0);
    }

    getTotalTax(): number {
        if (this.purchaseOrder?.taxAmount !== undefined && this.purchaseOrder.taxAmount !== null) {
            return Number(this.purchaseOrder.taxAmount);
        }
        if (!this.purchaseOrder?.purchaseOrderLines?.length) {
            return 0;
        }
        return this.purchaseOrder.purchaseOrderLines.reduce((total, line) => total + (line.totalTax ?? 0), 0);
    }

    getTotalAmount(): number {
        if (this.purchaseOrder?.totalAmount !== undefined && this.purchaseOrder.totalAmount !== null) {
            return Number(this.purchaseOrder.totalAmount);
        }
        if (!this.purchaseOrder?.purchaseOrderLines?.length) {
            return 0;
        }
        return this.purchaseOrder.purchaseOrderLines.reduce((total, line) => total + (line.totalLineAmount ?? (line.quantity * line.unitPrice)), 0);
    }

    getStatusSeverity(state: string | undefined): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' {
        return getOrderStatusMetadata(state).severity;
    }

    getStatusLabel(state: string | undefined): string {
        return getOrderStatusMetadata(state).label;
    }

    getStatusTooltip(state: string | undefined): string {
        return getOrderStatusMetadata(state).tooltip;
    }

    heroSubtitle(): string {
        return [
            this.getVendorName(),
            this.purchaseOrder?.orderDate ? `Order ${new Date(this.purchaseOrder.orderDate).toLocaleDateString()}` : null,
            this.purchaseOrder?.expectedDate ? `Expected ${new Date(this.purchaseOrder.expectedDate).toLocaleDateString()}` : null,
            this.formatMoney(this.getTotalAmount())
        ].filter(Boolean).join(' | ');
    }

    formatMoney(amount: number | undefined | null): string {
        return amount !== undefined && amount !== null ? `${this.currencyCode} ${Number(amount).toFixed(currencyFractionDigits(this.currencyCode))}` : '-';
    }

    detailMetrics(): PageMetric[] {
        return [
            { label: 'Order Total', value: this.formatMoney(this.getTotalAmount()), hint: `${this.currencyCode} base amount`, tone: 'success' },
            { label: 'Lines', value: this.getLineCount(), hint: 'Procurement lines', tone: 'info' },
            { label: 'Tax', value: this.formatMoney(this.getTotalTax()), hint: this.getLineCount() ? 'Calculated by line' : 'No lines' },
            { label: 'Expected', value: this.purchaseOrder?.expectedDate ? new Date(this.purchaseOrder.expectedDate).toLocaleDateString() : '-', hint: this.purchaseOrder?.paymentTerm?.name || 'No payment term', tone: 'warn' }
        ];
    }

    canRunAction(actionCode: string): boolean {
        return this.actionPermissionAllowed(actionCode)
            && !!this.purchaseActionMetadata?.availableActions?.includes(actionCode);
    }

    visibleHeaderLifecycleActions(): HeroLifecycleAction[] {
        return this.heroLifecycleActions.filter(action => this.canRunAction(action.code));
    }

    showEditAction(): boolean {
        return this.permissionService.hasPermission(app_permissions.order.records.edit);
    }

    showCreateAction(): boolean {
        return this.permissionService.hasPermission(app_permissions.order.records.create);
    }

    openAction(actionCode: string): void {
        if (this.actionLoading || !this.canRunAction(actionCode)) {
            return;
        }
        this.pendingAction = actionCode;
        this.actionReason = '';
        if (this.actionRequiresDialog(actionCode)) {
            this.actionDialogVisible = true;
            return;
        }
        this.runAction();
    }

    runAction(): void {
        if (!this.purchaseOrder?.purchaseOrderID || !this.pendingAction || !this.canRunAction(this.pendingAction)) {
            return;
        }
        if (this.actionRequiresReason(this.pendingAction) && !this.actionReason.trim()) {
            this.messageService.add({ severity: 'warn', summary: 'Reason required', detail: 'Enter a reason before continuing.' });
            return;
        }
        this.actionLoading = true;
        this.ordersService.runPurchaseOrderAction(
            this.purchaseOrder.purchaseOrderID,
            this.getActionPath(this.pendingAction),
            this.userId(),
            this.actionReason || undefined
        ).subscribe({
            next: order => {
                this.purchaseOrder = order;
                this.notesDraft = order.notes || '';
                this.removeCompletedAction(this.pendingAction);
                this.actionDialogVisible = false;
                this.actionLoading = false;
                this.messageService.add({ severity: 'success', summary: 'Updated', detail: `${this.getActionLabel(this.pendingAction)} completed.` });
                this.reloadOrderContext();
            },
            error: error => {
                this.actionLoading = false;
                this.messageService.add({ severity: 'error', summary: 'Action failed', detail: error?.error?.message || error?.error?.error || 'Unable to run purchase order action.' });
            }
        });
    }

    actionRequiresReason(actionCode: string): boolean {
        return !!this.purchaseActionMetadata?.requiresReason?.includes(actionCode)
            || ['REJECT', 'HOLD', 'RELEASE_HOLD', 'CANCEL'].includes(actionCode);
    }

    actionRequiresDialog(actionCode: string): boolean {
        return this.actionRequiresReason(actionCode);
    }

    blockedReasonFor(actionCode: string): string {
        return this.purchaseActionMetadata?.blockedReasons?.[actionCode] || '';
    }

    private actionPermissionAllowed(actionCode: string): boolean {
        if (['APPROVE', 'REJECT', 'CONFIRM'].includes(actionCode)) {
            return this.permissionService.hasPermission(app_permissions.order.records.approve);
        }
        if (actionCode === 'CANCEL') {
            return this.permissionService.hasPermission(app_permissions.order.records.cancel);
        }
        return this.permissionService.hasPermission(app_permissions.order.records.edit);
    }

    getActionLabel(actionCode: string): string {
        return actionCode.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, char => char.toUpperCase());
    }

    getActionPath(actionCode: string): string {
        return actionCode.toLowerCase().replace(/_/g, '-');
    }

    getLineTaxLabel(line: PurchaseOrderLine): string {
        const rate = line.taxRate !== undefined && line.taxRate !== null ? ` ${Number(line.taxRate).toFixed(2)}%` : '';
        return `${line.taxCode || 'Tax'}${rate}`;
    }

    getVendorName(): string {
        if (this.crmActive()) {
            return this.purchaseOrder?.crmClient?.companyName || this.purchaseOrder?.supplier?.name || '-';
        }
        const supplierName = this.purchaseOrder?.supplier?.name;
        const vendorName = (this.purchaseOrder as any)?.vendor?.name;
        return supplierName ?? vendorName ?? '-';
    }

    onTabChange(value: string | number): void {
        this.activeTabIndex = Number(value);
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { tab: this.tabKeys[this.activeTabIndex] || this.tabKeys[0] },
            queryParamsHandling: 'merge',
            replaceUrl: true
        });
    }

    getLines(): PurchaseOrderLine[] {
        return this.purchaseOrder?.purchaseOrderLines ?? [];
    }

    getLineBaseAmount(line: PurchaseOrderLine): number {
        return line.totalLineAmount != null && line.totalTax != null
            ? Number(line.totalLineAmount) - Number(line.totalTax) + Number(line.appliedLineDiscount || 0)
            : Number(line.quantity || 0) * Number(line.unitPrice || 0);
    }

    getLineTotal(line: PurchaseOrderLine): number {
        return Number(line.totalLineAmount ?? (this.getLineBaseAmount(line) + Number(line.totalTax || 0)));
    }

    getDiscountAmount(): number {
        if (this.purchaseOrder?.discountAmount !== undefined && this.purchaseOrder.discountAmount !== null) {
            return Number(this.purchaseOrder.discountAmount);
        }
        return this.getLines().reduce((total, line) => total + Number(line.appliedLineDiscount || 0), 0);
    }

    getLineCount(): number {
        return this.getLines().length;
    }

    getApprovalStatus(): string {
        return this.purchaseOrder?.approvalStatus || 'NOT_REQUIRED';
    }

    getReceivingStatus(): string {
        return this.purchaseOrder?.receivingStatus || 'NOT_RECEIVED';
    }

    getBillingStatus(): string {
        return this.purchaseOrder?.billingStatus || 'NOT_BILLED';
    }

    canPostToAccounts(): boolean {
        return !!(this.vendorBillReadiness?.canStage ?? this.vendorBillReadiness?.ready)
            && this.permissionService.hasPermission(app_permissions.order.records.edit);
    }

    postToAccounts(): void {
        const id = this.purchaseOrder?.purchaseOrderID;
        if (!id || !this.canPostToAccounts() || this.payableStagingLoading) {
            return;
        }
        this.payableStagingLoading = true;
        this.ordersService.stagePurchaseOrderForConsolidation(id, {
            actorId: this.userId(),
            owningCompanyId: this.purchaseOrder?.owningCompanyId
        }).subscribe({
            next: result => {
                this.payableStagingLoading = false;
                this.messageService.add({
                    severity: result.warnings?.length ? 'warn' : 'success',
                    summary: result.stagingOutcome === 'UPDATED' ? 'Payable updated' :
                        result.stagingOutcome?.startsWith('SKIPPED') ? 'Payable unchanged' : 'Payable staged',
                    detail: result.warnings?.join(' ') || (result.stagingOutcome?.startsWith('SKIPPED')
                        ? 'The existing payable was left unchanged for finance review.' : 'Ready in To Bill.')
                });
                if (!result.stagingOutcome?.startsWith('SKIPPED')) {
                    this.router.navigate(['/accounts/enterprise/payables/to-be-billed/view', result.payableId]);
                } else {
                    this.reloadOrderContext();
                }
            },
            error: error => {
                this.payableStagingLoading = false;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Staging failed',
                    detail: error?.error?.message || 'Unable to stage payable for consolidation.'
                });
                this.reloadOrderContext();
            }
        });
    }

    getMatchingStatus(): string {
        return this.purchaseOrder?.matchingStatus || 'NOT_STARTED';
    }

    getHoldStatus(): string {
        return this.purchaseOrder?.holdStatus || 'CLEAR';
    }

    getCompanyName(): string {
        return this.purchaseOrder?.company?.name || '-';
    }

    getCompanyOrCrmLabel(): string {
        return this.crmActive() ? 'CRM Client' : 'Company';
    }

    getCompanyOrCrmValue(): string {
        return this.crmActive()
            ? (this.purchaseOrder?.crmClient?.companyName || '-')
            : this.getCompanyName();
    }

    selectedCrmContact() {
        const selectedId = this.purchaseOrder?.crmClientContactId || this.purchaseOrder?.crmClientContact?.id;
        if (!selectedId) {
            return this.purchaseOrder?.crmClientContact;
        }
        return this.purchaseOrder?.crmClientContact
            || (this.purchaseOrder?.crmClientContacts || []).find(contact => contact.id === selectedId);
    }

    selectedCrmContactLabel(): string {
        const contact = this.selectedCrmContact();
        if (!contact) {
            return (this.purchaseOrder?.crmClientContacts || []).length ? 'No CRM contact selected' : '-';
        }
        return [
            contact.name,
            contact.communicationType,
            contact.email,
            contact.phone || contact.mobile
        ].filter(Boolean).join(' | ');
    }

    getCrmContactLabels(): string[] {
        return (this.purchaseOrder?.crmClientContacts || []).map(contact =>
            [
                contact.name,
                contact.communicationType,
                contact.email,
                contact.phone || contact.mobile
            ].filter(Boolean).join(' | ')
        ).filter(Boolean);
    }

    crmActive(): boolean {
        return this.enterpriseFeatureService.getIsCrmSubscribed();
    }

    startNotesEdit(): void {
        this.notesDraft = this.purchaseOrder?.notes || '';
        this.notesEditing = true;
    }

    cancelNotesEdit(): void {
        this.notesDraft = this.purchaseOrder?.notes || '';
        this.notesEditing = false;
    }

    saveNotes(): void {
        if (!this.purchaseOrder?.purchaseOrderID) {
            return;
        }
        this.notesSaving = true;
        this.ordersService.updatePurchaseOrderNotes(this.purchaseOrder.purchaseOrderID, this.notesDraft || '', this.userId()).subscribe({
            next: order => {
                this.purchaseOrder = order;
                this.notesDraft = order.notes || '';
                this.notesEditing = false;
                this.notesSaving = false;
                this.messageService.add({ severity: 'success', summary: 'Notes saved', detail: 'Internal notes were updated.' });
            },
            error: error => {
                this.notesSaving = false;
                this.messageService.add({ severity: 'error', summary: 'Notes failed', detail: error?.error?.message || error?.error?.error || 'Unable to update internal notes.' });
            }
        });
    }

    onPurchaseOrderFileUpload(event: any): void {
        const file: File | undefined = event.files?.[0];
        if (!file || !this.purchaseOrder?.purchaseOrderID) {
            return;
        }
        const orderId = this.purchaseOrder.purchaseOrderID;
        const owningCompanyId = this.purchaseOrder.owningCompanyId || this.sessionStorageService.getCompany()?.id || 0;
        if (!owningCompanyId) {
            this.messageService.add({ severity: 'warn', summary: 'Company missing', detail: 'Cannot link the file without an owning company.' });
            return;
        }
        this.attachmentUploading = true;
        this.attachmentUploadProgress = 0;
        this.firebaseStorageService.uploadFileWithProgress(file, `orders/purchase-orders/${orderId}/attachments`).subscribe({
            next: upload => {
                this.attachmentUploadProgress = upload.progress;
                if (!upload.url) {
                    return;
                }
                const attachment: PurchaseOrderAttachment = {
                    attachmentId: 0,
                    purchaseOrderId: orderId,
                    owningCompanyId,
                    fileName: file.name,
                    filePath: upload.url,
                    fileType: file.type || 'Attachment',
                    fileSize: file.size,
                    uploadedAt: new Date(),
                    uploadedBy: this.userId(),
                    fileContent: ''
                };
                this.ordersService.createPurchaseOrderAttachment(orderId, owningCompanyId, attachment).subscribe({
                    next: saved => {
                        this.purchaseAttachments = [saved, ...this.purchaseAttachments];
                        if (this.purchaseOrder) {
                            this.purchaseOrder.attachments = this.purchaseAttachments;
                        }
                        this.attachmentUploading = false;
                        this.attachmentUploadProgress = 0;
                        this.messageService.add({ severity: 'success', summary: 'Uploaded', detail: `${file.name} uploaded.` });
                    },
                    error: error => {
                        this.attachmentUploading = false;
                        this.attachmentUploadProgress = 0;
                        this.messageService.add({
                            severity: 'error',
                            summary: 'Link failed',
                            detail: error?.error?.message || error?.error?.error
                                || (typeof error?.error === 'string' ? error.error : null)
                                || error?.message
                                || 'File uploaded to Firebase, but could not be linked to the purchase order.'
                        });
                    }
                });
            },
            error: error => {
                this.attachmentUploading = false;
                this.attachmentUploadProgress = 0;
                this.messageService.add({ severity: 'error', summary: 'Upload failed', detail: error?.message || 'Unable to upload file.' });
            }
        });
    }

    downloadAttachment(file: PurchaseOrderAttachment): void {
        if (file.filePath) {
            window.open(file.filePath, '_blank');
        }
    }

    openAttachmentDelete(file: PurchaseOrderAttachment): void {
        if (!this.showEditAction() || !file.attachmentId) return;
        this.attachmentToDelete = file;
        this.attachmentDeleteReason = '';
        this.attachmentDeleteError = '';
        this.attachmentDeleteDialogVisible = true;
    }

    deleteAttachment(): void {
        const file = this.attachmentToDelete;
        if (!file?.attachmentId || !this.showEditAction() || !this.attachmentDeleteReason.trim() || this.attachmentDeleting) return;
        this.attachmentDeleting = true;
        this.attachmentDeleteError = '';
        this.ordersService.deletePurchaseOrderAttachment(file.attachmentId, this.attachmentDeleteReason.trim()).subscribe({
            next: () => {
                this.purchaseAttachments = this.purchaseAttachments.filter(item => item.attachmentId !== file.attachmentId);
                if (this.purchaseOrder) this.purchaseOrder.attachments = this.purchaseAttachments;
                this.attachmentToDelete = undefined;
                this.attachmentDeleteDialogVisible = false;
                this.attachmentDeleting = false;
                this.messageService.add({ severity: 'success', summary: 'Attachment removed', detail: `${file.fileName} was removed from this order.` });
                this.reloadOrderContext();
            },
            error: error => {
                this.attachmentDeleting = false;
                this.attachmentDeleteError = error?.error?.message || 'Could not remove this attachment.';
                this.messageService.add({ severity: 'error', summary: 'Removal failed', detail: this.attachmentDeleteError });
            }
        });
    }

    formatFileSize(size: number | undefined | null): string {
        if (!size) {
            return '0 MB';
        }
        return `${(Number(size) / 1024 / 1024).toFixed(2)} MB`;
    }

    openVendorBills(): void {
        this.router.navigate(['/accounts/enterprise/payables/generated-bills'], {
            queryParams: { purchaseOrderId: this.purchaseOrder?.purchaseOrderID || null }
        });
    }

    openMatchingDashboard(): void {
        this.router.navigate(['/accounts/enterprise/payables/matching-dashboard'], {
            queryParams: { purchaseOrderId: this.purchaseOrder?.purchaseOrderID || null }
        });
    }

    private userId(): number {
        return this.sessionStorageService.getCustomer()?.id ?? this.sessionStorageService.getUser()?.id ?? 0;
    }

    private reloadOrderContext(): void {
        if (!this.purchaseOrder?.purchaseOrderID) {
            return;
        }
        forkJoin({
            order: this.ordersService.getPurchaseOrderById(this.purchaseOrder.purchaseOrderID),
            activity: this.ordersService.getPurchaseOrderActivity(this.purchaseOrder.purchaseOrderID)
                .pipe(catchError(() => of([]))),
            actions: this.ordersService.getPurchaseOrderActions(this.purchaseOrder.purchaseOrderID)
                .pipe(catchError(() => of({ availableActions: [], requiresReason: [], blockedReasons: {} }))),
            vendorBillReadiness: this.ordersService.getPurchaseOrderVendorBillReadiness(this.purchaseOrder.purchaseOrderID)
                .pipe(catchError(() => of(undefined)))
        }).subscribe(({ order, activity, actions, vendorBillReadiness }) => {
            this.purchaseOrder = order;
            this.activity = activity ?? [];
            this.purchaseActionMetadata = actions || {};
            this.currencyCode = order.currencyCode || this.currencyCode;
            this.notesDraft = order.notes || '';
            this.purchaseAttachments = order.attachments ?? [];
            this.vendorBillReadiness = vendorBillReadiness;
        });
    }

    private removeCompletedAction(actionCode: string): void {
        const availableActions = (this.purchaseActionMetadata?.availableActions || [])
            .filter((action: string) => action !== actionCode);
        this.purchaseActionMetadata = {
            ...this.purchaseActionMetadata,
            availableActions
        };
    }

    private resolveTabIndex(tab: string | null): number {
        const index = this.tabKeys.findIndex(value => value === (tab || '').toLowerCase());
        return index >= 0 ? index : 0;
    }
}
